import { format } from "date-fns";
import type { LeadInteraction } from "../types";
import { INTERACTION_EMOJI } from "../constants";
import { useLocale } from "../../../context/LanguageContext";

/** Parse "Status changed: OLD → NEW. optional note" into structured parts */
function parseStatusChange(content: string): { from: string; to: string; note?: string } | null {
  const match = content.match(/^Status changed:\s*([\w]+)\s*→\s*([\w]+)\.?\s*(.*)?$/);
  if (!match) return null;
  return {
    from: match[1],
    to: match[2],
    note: match[3]?.trim() || undefined,
  };
}

interface LeadInteractionTimelineProps {
  interactions: LeadInteraction[];
}

export function LeadInteractionTimeline({ interactions }: LeadInteractionTimelineProps) {
  const { t } = useLocale();

  // Show most recent first
  const sorted = [...interactions].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  if (sorted.length === 0) {
    return (
      <p className="py-4 text-center text-sm text-muted-foreground">
        {t("leads.detail.noInteractions")}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {sorted.map((interaction, idx) => {
        const isStatusChange = interaction.type === "STATUS_CHANGE";
        const parsed = isStatusChange ? parseStatusChange(interaction.content) : null;

        return (
          <div key={idx} className="flex gap-3">
            <span className="mt-0.5 text-lg">
              {INTERACTION_EMOJI[interaction.type]}
            </span>
            <div className="flex-1">
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-medium text-muted-foreground">
                  {t(`leads.interaction.${interaction.type}`)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {format(new Date(interaction.createdAt), "MMM d, yyyy HH:mm")}
                </span>
              </div>

              {isStatusChange && parsed ? (
                <div className="mt-1 space-y-1">
                  <p className="text-sm text-foreground">
                    {t(`leads.status.${parsed.from}`)} → {t(`leads.status.${parsed.to}`)}
                  </p>
                  {parsed.note && (
                    <p className="rounded-md bg-muted/50 px-2 py-1 text-xs text-muted-foreground italic">
                      {parsed.note}
                    </p>
                  )}
                </div>
              ) : (
                <p className="mt-0.5 text-sm text-foreground">
                  {interaction.content}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
